import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import {
  FaQrcode, FaSearch, FaCheckCircle, FaExclamationCircle,
  FaUserCheck, FaCamera, FaUndo, FaIdCard, FaPhoneAlt, FaChair, FaUsers
} from 'react-icons/fa';
import { Html5Qrcode } from 'html5-qrcode';
import { invitationApi } from '../../api/guestList/invitationApi';
import './ScanCardModal.css';

const ScanCardModal = ({ show, onHide, onAttendanceMarked }) => {
  const [cardNumberInput, setCardNumberInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [marking, setMarking] = useState(false);
  const [scannedGuest, setScannedGuest] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);

  // Attendance Options
  const [guestCount, setGuestCount] = useState(1);
  const [remarksInput, setRemarksInput] = useState('Sawa');

  const qrRegionId = 'qr-code-reader-box-element';
  const html5QrcodeRef = useRef(null);

  // Start camera when modal shows
  useEffect(() => {
    if (show && !scannedGuest) {
      const timer = setTimeout(() => {
        startCamera();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [show, scannedGuest]);

  const startCamera = async () => {
    setScanError(null);
    try {
      if (!html5QrcodeRef.current) {
        html5QrcodeRef.current = new Html5Qrcode(qrRegionId);
      }
      if (html5QrcodeRef.current.isScanning) {
        return;
      }

      // Explicitly prompt for getUserMedia permission if available
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
          stream.getTracks().forEach(track => track.stop());
        } catch (mediaErr) {
          console.warn('Explicit getUserMedia prompt result:', mediaErr);
        }
      }

      const config = {
        fps: 15,
        aspectRatio: 2.0,
        qrbox: (viewfinderWidth, viewfinderHeight) => ({
          width: Math.floor(viewfinderWidth),
          height: Math.floor(viewfinderHeight)
        })
      };

      // Try device selection first using getCameras
      try {
        const devices = await Html5Qrcode.getCameras();
        if (devices && devices.length > 0) {
          const backCam = devices.find(d =>
            d.label.toLowerCase().includes('back') ||
            d.label.toLowerCase().includes('rear') ||
            d.label.toLowerCase().includes('environment')
          );
          const cameraId = backCam ? backCam.id : devices[devices.length - 1].id;

          await html5QrcodeRef.current.start(
            cameraId,
            config,
            onQrCodeSuccess,
            onQrCodeFailure
          );
          setCameraActive(true);
          return;
        }
      } catch (camErr) {
        console.warn('getCameras fallback check:', camErr);
      }

      // Fallback start with facingMode environment
      await html5QrcodeRef.current.start(
        { facingMode: 'environment' },
        config,
        onQrCodeSuccess,
        onQrCodeFailure
      );
      setCameraActive(true);
    } catch (err) {
      console.error('Camera start error:', err);
      setCameraActive(false);
      setScanError('Imeshindwa kufungua kamera. Hakikisha umeipa tovuti ruhusa ya kutumia kamera (Camera Permission), au ingiza namba ya kadi hapo chini.');
    }
  };

  const stopCamera = async () => {
    if (html5QrcodeRef.current && html5QrcodeRef.current.isScanning) {
      try {
        await html5QrcodeRef.current.stop();
        html5QrcodeRef.current.clear();
      } catch (e) {
        console.warn('Error stopping camera:', e);
      }
    }
    setCameraActive(false);
  };

  const onQrCodeFailure = (error) => {
    // Routine frame parse error ignore
  };

  const extractCardNumber = (scannedText) => {
    if (!scannedText) return '';
    let str = String(scannedText).trim();
    try {
      const parsed = JSON.parse(str);
      if (parsed.card_number) return parsed.card_number;
      if (parsed.card) return parsed.card;
      if (parsed.code) return parsed.code;
    } catch (e) { }

    if (str.includes('http') || str.includes('/')) {
      const parts = str.split('/');
      const lastPart = parts[parts.length - 1];
      if (lastPart && !lastPart.includes('http')) {
        str = lastPart.split('?')[0];
      }
    }
    return str.trim();
  };

  const onQrCodeSuccess = (decodedText) => {
    const cardNum = extractCardNumber(decodedText);
    if (cardNum) {
      stopCamera();
      setCardNumberInput(cardNum);
      handleSearchCard(cardNum);
    }
  };

  const handleSearchCard = async (cardNumToSearch) => {
    const cardNum = cardNumToSearch || cardNumberInput;
    if (!cardNum || !cardNum.trim()) {
      setScanError('Tafadhali ingiza au scan namba ya kadi');
      return;
    }
    setSearching(true);
    setScanError(null);
    setScannedGuest(null);

    const cleanCard = cardNum.trim();
    const query = cleanCard.toLowerCase();

    try {
      let guestData = null;

      // 1. Primary Search via POST /api/guests/search-by-card
      try {
        const response = await invitationApi.searchByCard(cleanCard);
        const payload = response?.data?.data || response?.data || response || {};
        if (payload && typeof payload === 'object') {
          if (payload.guest || payload.invitation) {
            guestData = payload.guest || payload.invitation;
          } else if (payload.id || payload.card_number || payload.name || payload.full_name) {
            guestData = payload;
          } else if (Array.isArray(payload) && payload.length > 0) {
            guestData = payload[0];
          }
        }
      } catch (e) {
        console.warn('search-by-card API primary check:', e);
      }

      // 2. Secondary Search via GET /api/guests/attendance
      if (!guestData) {
        let attRes = await invitationApi.getAttendanceList({ search: cleanCard });
        let list = attRes?.data?.data || attRes?.data || attRes || [];

        if (!Array.isArray(list) && Array.isArray(attRes?.data?.data)) {
          list = attRes.data.data;
        }
        if (!Array.isArray(list) && Array.isArray(attRes?.data)) {
          list = attRes.data;
        }

        if (!Array.isArray(list) || list.length === 0) {
          const fullAttRes = await invitationApi.getAttendanceList();
          const fullData = fullAttRes?.data?.data || fullAttRes?.data || fullAttRes || [];
          if (Array.isArray(fullData)) {
            list = fullData;
          }
        }

        if (Array.isArray(list) && list.length > 0) {
          guestData = list.find(g => {
            const cNum = String(g.card_number || g.cardNumber || g.code || '').trim().toLowerCase();
            const gName = String(g.name || g.full_name || g.guest_name || '').trim().toLowerCase();
            const gPhone = String(g.phone || g.phone_number || g.mobile || '').trim().toLowerCase();
            return cNum === query || cNum.includes(query) || query.includes(cNum) || gName === query || gPhone === query;
          });
        }
      }

      if (guestData) {
        const isAttended =
          Number(guestData.attended) === 1 ||
          Number(guestData.attendance_status) === 1 ||
          Number(guestData.is_attended) === 1 ||
          Number(guestData.status) === 1 ||
          guestData.attended === true ||
          guestData.attendance === 1;

        const maxSeats = Number(guestData.max_seats || guestData.seats || (Number(guestData.card_type) === 2 ? 2 : 1)) || 1;
        const initialGuestCount = Number(guestData.guest_count) || 1;

        setGuestCount(initialGuestCount);
        setRemarksInput('Sawa');

        setScannedGuest({
          id: guestData.guest_lists_id || guestData.guest_list_id || guestData.guest_id || guestData.id,
          guest_lists_id: guestData.guest_lists_id || guestData.guest_list_id || guestData.guest_id || guestData.id,
          name: guestData.name || guestData.full_name || guestData.guest_name || 'Guest',
          phone: guestData.phone_number || guestData.mobile || guestData.phone || '—',
          cardNumber: guestData.card_number || guestData.cardNumber || cleanCard,
          cardType: maxSeats > 1 ? `Double (Viti ${maxSeats})` : `Single (Kiti 1)`,
          maxSeats: maxSeats,
          tableNumber: guestData.table_number || guestData.table || '—',
          attendanceStatus: isAttended ? 'ALREADY_USED' : 'PENDING'
        });
      } else {
        setScanError(`Kadi namba "${cleanCard}" haijapatikana au haijahusishwa na mgeni wowote.`);
      }
    } catch (err) {
      console.error('Error searching card:', err);
      const msg = err.response?.data?.message || err.message || 'Imeshindwa kutafuta kadi. Jaribu tena.';
      setScanError(msg);
    } finally {
      setSearching(false);
    }
  };

  const handleMarkAttendance = async () => {
    if (!scannedGuest) return;
    setMarking(true);
    try {
      const guestListId = scannedGuest.guest_lists_id || scannedGuest.id;
      const payload = {
        guest_lists_id: Number(guestListId) || guestListId,
        guest_count: Number(guestCount) || 1,
        ...(remarksInput.trim() ? { remarks: remarksInput.trim() } : {})
      };
      const response = await invitationApi.markAttendance(payload);

      if (response?.success !== false && response?.status !== 400) {
        setScannedGuest(prev => ({
          ...prev,
          attendanceStatus: 'JUST_VERIFIED'
        }));
        if (onAttendanceMarked) {
          onAttendanceMarked();
        }
      } else {
        setScanError(response?.message || 'Imeshindwa kuthibitisha mahudhurio');
      }
    } catch (err) {
      console.error('Error marking attendance:', err);
      setScanError(err.response?.data?.message || err.message || 'Imeshindwa kuthibitisha mahudhurio');
    } finally {
      setMarking(false);
    }
  };

  const handleResetScan = () => {
    setScannedGuest(null);
    setScanError(null);
    setCardNumberInput('');
    setTimeout(() => startCamera(), 200);
  };

  const handleClose = () => {
    stopCamera();
    setScannedGuest(null);
    setScanError(null);
    setCardNumberInput('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="scan-card-modal">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2 fw-bold text-dark">
          <div className="scan-modal-icon-badge">
            <FaQrcode size={18} color="#4f46e5" />
          </div>
          <div>
            <div style={{ fontSize: '1.05rem', color: '#1e293b', lineHeight: '1.2' }}>Hakiki Kadi na Mahudhurio</div>
            <div className="text-muted fw-normal" style={{ fontSize: '0.75rem' }}>
              Scan QR Code au ingiza namba ya kadi
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {!scannedGuest ? (
          <div>
            {/* 1. Camera QR Scanner */}
            <div className="camera-scanner-wrapper text-center mb-3">
              <div id={qrRegionId} className="qr-scanner-box"></div>
              {!cameraActive && !scanError && (
                <div className="p-3 text-center text-muted">
                  <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                  Inafungua kamera ya QR code...
                </div>
              )}
            </div>

            <div className="text-center my-2 text-muted small fw-semibold" style={{ fontSize: '0.8rem' }}>
              ── AU INGIZA NAMBA YA KADI ──
            </div>

            {/* 2. Manual Input Form directly below camera */}
            <div className="manual-input-wrapper p-3 bg-light rounded-4 border mt-2">
              <Form onSubmit={(e) => { e.preventDefault(); handleSearchCard(); }}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-secondary small">Ingiza Namba ya Kadi (Card Number)</Form.Label>
                  <div className="position-relative">
                    <FaIdCard className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                    <Form.Control
                      type="text"
                      placeholder="e.g. 1A, 44B, 72D..."
                      value={cardNumberInput}
                      onChange={(e) => setCardNumberInput(e.target.value.toUpperCase())}
                      className="ps-5 py-2 shadow-none border"
                      style={{ borderRadius: '12px', fontSize: '0.95rem' }}
                    />
                  </div>
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={searching || !cardNumberInput.trim()}
                  className="w-100 py-2.5 fw-semibold rounded-3 shadow-sm border-0 d-flex align-items-center justify-content-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', color: '#ffffff' }}
                >
                  {searching ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Inathibitisha taarifa...
                    </>
                  ) : (
                    <>
                      <FaSearch /> Hakiki Kadi
                    </>
                  )}
                </Button>
              </Form>

              {scanError && (
                <Alert variant="danger" className="mt-3 small border-0 shadow-sm mb-0" style={{ backgroundColor: '#fef2f2', color: '#991b1b' }}>
                  <FaExclamationCircle className="me-2 flex-shrink-0" />
                  {scanError}
                </Alert>
              )}
            </div>
          </div>
        ) : (
          /* Scanned Guest Result Container */
          <div className="scanned-result-card bg-white p-3 rounded-4 border shadow-sm">
            {/* Header badges */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              {scannedGuest.attendanceStatus === 'JUST_VERIFIED' ? (
                <span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
                  <FaCheckCircle size={13} /> Amehudhuria
                </span>
              ) : scannedGuest.attendanceStatus === 'ALREADY_USED' ? (
                <span className="badge rounded-pill bg-danger-subtle text-danger border border-danger-subtle px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
                  <FaExclamationCircle size={13} /> Imeshatumika
                </span>
              ) : (
                <span className="badge rounded-pill bg-warning-subtle text-dark border border-warning-subtle px-3 py-1.5 fw-semibold" style={{ fontSize: '0.8rem' }}>
                  Inasubiri Kuthibitishwa
                </span>
              )}

              <span className="badge rounded-pill text-white px-3 py-1.5 font-monospace fw-semibold" style={{ fontSize: '0.8rem', backgroundColor: '#334155' }}>
                Kadi: {scannedGuest.cardNumber}
              </span>
            </div>

            {/* Guest identity header */}
            <div className="d-flex align-items-center gap-3 mb-3 pb-2 border-bottom">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                style={{ width: '46px', height: '46px', background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', fontSize: '1.15rem', flexShrink: 0 }}
              >
                {scannedGuest.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '1.1rem' }}>{scannedGuest.name}</h5>
                <a href={`tel:${scannedGuest.phone}`} className="text-decoration-none text-muted small d-inline-flex align-items-center gap-1 mt-0.5">
                  <FaPhoneAlt size={11} className="text-primary" /> {scannedGuest.phone}
                </a>
              </div>
            </div>

            {/* Guest Card Info Grid */}
            <div className="bg-light p-3 rounded-3 border mb-3">
              <div className="row g-2 text-start">
                <div className="col-6">
                  <div className="text-muted small">Aina ya Kadi:</div>
                  <div className="fw-semibold text-dark small mt-0.5">{scannedGuest.cardType}</div>
                </div>
                <div className="col-6">
                  <div className="text-muted small">Meza:</div>
                  <div className="fw-semibold text-dark small mt-0.5">{scannedGuest.tableNumber}</div>
                </div>
              </div>
            </div>

            {/* If pending attendance, show controls for Guest Count & Remarks */}
            {scannedGuest.attendanceStatus === 'PENDING' ? (
              <div className="mb-3">
                <div className="row g-2 mb-2 text-start">
                  <div className="col-6">
                    <Form.Label className="small fw-semibold text-secondary mb-1">Idadi ya Wageni</Form.Label>
                    <Form.Select
                      size="sm"
                      value={guestCount}
                      onChange={(e) => setGuestCount(Number(e.target.value))}
                      className="rounded-2 shadow-none border"
                      style={{ fontSize: '0.85rem' }}
                    >
                      {Array.from({ length: Math.max(1, scannedGuest.maxSeats || 2) }, (_, i) => i + 1).map(num => (
                        <option key={num} value={num}>
                          {num === 1 ? 'Mgeni 1' : `Wageni ${num}`}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-6">
                    <Form.Label className="small fw-semibold text-secondary mb-1">Maelezo (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="e.g. Sawa"
                      value={remarksInput}
                      onChange={(e) => setRemarksInput(e.target.value)}
                      className="rounded-2 shadow-none border"
                      style={{ fontSize: '0.85rem' }}
                    />
                  </div>
                </div>

                <Button
                  variant="success"
                  onClick={handleMarkAttendance}
                  disabled={marking}
                  className="w-100 py-2.5 fw-semibold rounded-3 shadow-sm border-0 d-flex align-items-center justify-content-center gap-2 mt-3"
                  style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#ffffff' }}
                >
                  {marking ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Inathibitisha Mahudhurio...
                    </>
                  ) : (
                    <>
                      <FaUserCheck size={16} /> Thibitisha Mahudhurio
                    </>
                  )}
                </Button>
              </div>
            ) : scannedGuest.attendanceStatus === 'JUST_VERIFIED' ? (
              <Alert variant="success" className="text-center py-2.5 border-0 fw-semibold mb-3 rounded-3 shadow-sm" style={{ backgroundColor: '#dcfce7', color: '#15803d', fontSize: '0.88rem' }}>
                <FaCheckCircle className="me-2" /> Mahudhurio Yamekamilika Vizuri!
              </Alert>
            ) : (
              <Alert variant="danger" className="text-center py-2.5 border-0 fw-semibold mb-3 rounded-3 shadow-sm" style={{ backgroundColor: '#fef2f2', color: '#991b1b', fontSize: '0.88rem' }}>
                <FaExclamationCircle className="me-2" /> Kadi Hii Imeshatumika Kuingilia!
              </Alert>
            )}

            <Button
              variant="outline-secondary"
              onClick={handleResetScan}
              className="w-100 py-2 fw-semibold rounded-3 border-secondary-subtle d-flex align-items-center justify-content-center gap-2"
              style={{ fontSize: '0.85rem' }}
            >
              <FaUndo size={14} /> Scan Kadi Nyingine
            </Button>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="light" onClick={handleClose} className="px-4 fw-semibold text-secondary">
          Funga
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ScanCardModal;
